import './ProfileCard.scss';

import dayjs from 'dayjs';
import { AdminUserDetails } from "types/admin/adminUserDetails";
import { Button, Switch } from '@trackman/web-shared-components';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServices, useStores } from "index";
import { getErrorMessage } from "utilities/ErrorHelper";
import { ReactComponent as Copy } from 'images/icons/Copy.svg';

type Props = {
  userId: string;
  userDetails: AdminUserDetails;
};

export const ProfileCard = ({ userId, userDetails }: Props) => {
  const service = useServices().manageUserDataService;
  const { notificationStore } = useStores();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['adminUserDetails', userId] });

  const togglePremium = useMutation({
    mutationFn: (enable: boolean) =>
      enable ? service.addSupportMembership(userId!) : service.cancelSupportMembership(userId!),
    onSuccess: () => {
      notificationStore.addToast('success', 'Membership updated', 'success');
      invalidate();
    },
    onError: (e: Error) => notificationStore.addToast('error', getErrorMessage(e), 'error'),
  });

  const toggleMaster = useMutation({
    mutationFn: () => service.updatePlayerLevelMaster(userId!),
    onSuccess: () => {
      notificationStore.addToast('success', 'Master status granted', 'success');
      invalidate();
    },
    onError: (e: Error) => notificationStore.addToast('error', getErrorMessage(e), 'error'),
  });

  return (
    <div className='user-management-sidebar'>
      <div className='user-profile-card'>
        <div className='user-profile-identity'>
          {userDetails.avatarUrl
            ? <img src={userDetails.avatarUrl} className='user-avatar' alt='' />
            : <div className='user-avatar'>{userDetails.fullName?.[0]?.toUpperCase() ?? '?'}</div>
          }
          <div>
            <span className='user-profile-name'>{userDetails.fullName}</span>
            <span className='user-profile-email'>{userDetails.email}</span>
          </div>
        </div>
        <div className='user-toggles'>
          <div className='user-toggle-row'>
            <Switch
              label=''
              checked={userDetails.hasPremiumMembership}
              onChange={(_e: React.SyntheticEvent, checked: boolean) => togglePremium.mutate(checked)}
              disabled={togglePremium.isPending}
            />
            <div className='user-toggle-text'>
              <div className='user-toggle-label'>
                <strong>Support Premium membership:</strong>{' '}
                {userDetails.hasPremiumMembership ? 'Enabled' : 'Disabled'}
              </div>
              <div className='user-toggle-desc'>When enabled user will receive premium membership.</div>
            </div>
          </div>

          <div className='user-toggle-row'>
            <Switch
              label=''
              checked={userDetails.hasMasterStatus}
              onChange={() => !userDetails.hasMasterStatus && toggleMaster.mutate()}
              disabled={toggleMaster.isPending || userDetails.hasMasterStatus}
            />
            <div className='user-toggle-text'>
              <div className='user-toggle-label'>
                <strong>Trackman Master status:</strong>{' '}
                {userDetails.hasMasterStatus ? 'Enabled' : 'Disabled'}
              </div>
              <div className='user-toggle-desc'>When enabled user will receive Trackman Master status.</div>
            </div>
          </div>
        </div>

        <div className='user-id-row'>
          <div className='user-id-label'>User ID</div>
          <div className='user-id-value'>
            {userDetails.userId}
          </div>
          <Button
            className='copy-btn'
            size='small'
            title='Copy user ID'
            startIcon={<Copy />}
            onClick={() => navigator.clipboard.writeText(userDetails.userId)}
          >
          </Button>
        </div>

        {userDetails.organisations.map((org, i) => (
          <div key={i} className='user-org-row'>
            <div className='user-org-inf-row'>
              <span className='user-org-label'>Organisation</span>
              <span className='user-org-value'>{org.orgName}</span>
            </div>
            <div className='user-org-inf-row'>
              <span className='user-org-label'>Class</span>
              <span className='user-org-value'>{org.className ?? '-'}</span>
            </div>
          </div>
        ))}

        {userDetails.memberships.map((membership, i) => (
          <div key={i} className='user-org-row'>
            <div className='user-org-inf-row'>
              <span className='user-org-label'>Membership type</span>
              <span className='user-org-value'>{membership.type}</span>
            </div>
            <div className='user-org-inf-row'>
              <span className='user-org-label'>Expiry date</span>
              <span className='user-org-value'>{dayjs(membership.expirationDate).format('DD/MM/YYYY')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
};